import { CeConfig } from "~/types/interfaces/ce-config";

// Column-level shapes stored inside a CE configuration's `content.tables[].columns`.
// The shared `CeConfigDomain.columns` is typed `any[]`; these interfaces narrow the
// fields this read-only viewer needs. They mirror the backend `ColumnConfig`
// (product/cohort-explorer/src/api/configurator/schemas.py).

export interface ConfigFilter {
  filter_type?: string;
  name?: string;
  disabled?: boolean;
}

export interface ConfigVisualization {
  chart_type?: string | null;
  name?: string;
  disabled?: boolean;
}

export interface ConfigNamedToggle {
  name?: string;
  disabled?: boolean;
}

export interface ConfigExport {
  disabled?: boolean;
}

export interface ConfigColumn {
  column: string;
  filter?: ConfigFilter | null;
  visualization?: ConfigVisualization | null;
  breakdown?: ConfigNamedToggle | null;
  data?: ConfigNamedToggle | null;
  export?: ConfigExport | null;
}

export const FilterTone = {
  SELECT: "select",
  NUMERIC: "numeric",
  DATE: "date",
  OFF: "off",
} as const;
export type FilterTone = (typeof FilterTone)[keyof typeof FilterTone];

export interface FilterBadge {
  label: string;
  tone: FilterTone;
}

const FILTER_LABELS: Record<string, string> = {
  multiselect: "Multi-select",
  dependent_select_multiselect: "Dependent",
  numeric: "Numeric",
  date: "Date",
};

const FILTER_TONES: Record<string, FilterTone> = {
  multiselect: FilterTone.SELECT,
  dependent_select_multiselect: FilterTone.SELECT,
  numeric: FilterTone.NUMERIC,
  date: FilterTone.DATE,
};

const CHART_LABELS: Record<string, string> = {
  histogram: "Histogram",
  bar: "Bar",
  pie: "Pie",
  treemap: "Treemap",
  line: "Line",
  doughnut: "Doughnut",
  scatter: "Scatter",
  radar: "Radar",
  bubble: "Bubble",
  polarArea: "Polar area",
};

export function titleCase(value: string): string {
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function hasFilter(column: ConfigColumn): boolean {
  return Boolean(column.filter) && !column.filter?.disabled;
}

export function hasChart(column: ConfigColumn): boolean {
  return (
    Boolean(column.visualization) &&
    !column.visualization?.disabled &&
    Boolean(column.visualization?.chart_type)
  );
}

export function isViewable(column: ConfigColumn): boolean {
  return Boolean(column.data) && !column.data?.disabled;
}

export function isExportable(column: ConfigColumn): boolean {
  return Boolean(column.export) && !column.export?.disabled;
}

export function isBreakdown(column: ConfigColumn): boolean {
  return Boolean(column.breakdown) && !column.breakdown?.disabled;
}

export function isKeyColumn(column: ConfigColumn, tableKey?: string): boolean {
  return Boolean(tableKey) && column.column === tableKey;
}

export function columnDisplayName(column: ConfigColumn): string {
  return column.data?.name || column.filter?.name || column.column;
}

export function filterBadge(column: ConfigColumn): FilterBadge {
  if (!hasFilter(column)) {
    return { label: "Off", tone: FilterTone.OFF };
  }
  const type = column.filter?.filter_type;
  const label = (type && FILTER_LABELS[type]) || (type ? titleCase(type) : "On");
  const tone = (type && FILTER_TONES[type]) || FilterTone.SELECT;
  return { label, tone };
}

export function chartLabel(column: ConfigColumn): string {
  if (!hasChart(column)) {
    return "Off";
  }
  const type = column.visualization?.chart_type ?? "";
  return CHART_LABELS[type] ?? titleCase(type);
}

export interface DomainStats {
  columnCount: number;
  filterCount: number;
  chartCount: number;
  viewableCount: number;
  exportCount: number;
  breakdownCount: number;
}

export function domainStats(columns: ConfigColumn[], isPrimary: boolean): DomainStats {
  return {
    columnCount: columns.length,
    filterCount: columns.filter(hasFilter).length,
    chartCount: columns.filter(hasChart).length,
    viewableCount: columns.filter(isViewable).length,
    exportCount: columns.filter(isExportable).length,
    breakdownCount: isPrimary ? columns.filter(isBreakdown).length : 0,
  };
}

export interface ConfigVersion extends CeConfig {
  // 1-based display number, newest = highest.
  number: number;
  isLatest: boolean;
}

// Newest-first by version_id (monotonic per dataset), with a 1-based display number.
export function orderVersions(versions: CeConfig[]): ConfigVersion[] {
  const sorted = [...versions].sort((a, b) => b.version_id - a.version_id);
  const total = sorted.length;
  return sorted.map((version, index) => ({
    ...version,
    number: total - index,
    isLatest: index === 0,
  }));
}

export function formatVersionMeta(createdAt: string): string {
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) {
    return createdAt;
  }
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
